import React from 'react';

const ProcessSection: React.FC = () => {
  const steps = [
    {
      number: '01',
      title: 'Comprehensive Assessment',
      description: 'Detailed medical history, symptom evaluation, and lifestyle analysis to understand your unique needs.',
      details: ['Complete medical questionnaire', 'Symptom severity assessment', 'Lifestyle and stress evaluation', 'Previous treatment history review'],
      icon: 'ri-file-text-line'
    },
    {
      number: '02',
      title: 'Advanced Laboratory Testing',
      description: 'Comprehensive hormone panel and biomarker analysis to establish your baseline levels.',
      details: ['Complete hormone panel (testosterone, estrogen, progesterone, thyroid)', 'Metabolic markers and lipid profile', 'Vitamin D and B12 levels', 'Inflammatory markers assessment'],
      icon: 'ri-test-tube-line'
    },
    {
      number: '03',
      title: 'Personalized Treatment Plan',
      description: 'Custom protocol designed specifically for your hormone profile and health goals.',
      details: ['Individualized hormone dosing', 'Delivery method selection', 'Supplement recommendations', 'Lifestyle optimization plan'],
      icon: 'ri-file-chart-line'
    },
    {
      number: '04',
      title: 'Treatment Initiation',
      description: 'Begin your hormone optimization journey with careful monitoring and support.',
      details: ['Initial hormone administration', 'Side effect monitoring', 'Dosage adjustments as needed', '24/7 medical support access'],
      icon: 'ri-medicine-bottle-line'
    },
    {
      number: '05',
      title: 'Ongoing Monitoring',
      description: 'Regular follow-ups and lab work to ensure optimal results and safety.',
      details: ['Monthly progress evaluations', 'Quarterly lab work', 'Dosage optimization', 'Long-term health monitoring'],
      icon: 'ri-line-chart-line'
    },
    {
      number: '06',
      title: 'Lifestyle Integration',
      description: 'Comprehensive support to maximize your treatment results through lifestyle optimization.',
      details: ['Nutrition counseling', 'Exercise recommendations', 'Stress management techniques', 'Sleep optimization strategies'],
      icon: 'ri-heart-3-line'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">Treatment Journey</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our systematic approach ensures safe, effective hormone optimization with 
            personalized care every step of the way.
          </p>
        </div>

        <div className="space-y-12">
          {steps.map((step, index) => (
            <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12`}>
              <div className="flex-1">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full flex items-center justify-center mr-6">
                    <span className="text-white font-bold text-lg">{step.number}</span>
                  </div>
                  <div>
                    <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                </div>
                
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  {step.description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-3"></i>
                      <span className="text-gray-700">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex-1 flex justify-center">
                <div className="w-80 h-80 bg-gradient-to-br from-pink-100 to-purple-100 rounded-full flex items-center justify-center">
                  <i className={`${step.icon} text-8xl text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600`}></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Start Your Transformation?
            </h3>
            <p className="text-gray-600 mb-6">
              Schedule your comprehensive consultation today and take the first step toward optimal health.
            </p>
            <button className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold hover:from-pink-700 hover:to-purple-700 transition-all duration-300">
              <i className="ri-calendar-check-line mr-2"></i>
              Book Your Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;